/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    p: "p",
    a: "a",
    ul: "ul",
    li: "li",
    h2: "h2",
    code: "code",
    em: "em",
    h3: "h3",
    pre: "pre",
    strong: "strong"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h1, {
      children: "Maintaining themes"
    }), "\n", _jsx(_components.p, {
      children: "This section contains documentation about how to maintain and create a theme."
    }), "\n", _jsxs(_components.p, {
      children: ["You may also check out the section about ", _jsx(_components.a, {
        href: "/uilib/usage/customisation/theming/",
        children: "how to use a theme in your application"
      }), "."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#maintaining-themes",
          children: "Maintaining themes"
        }), "\n", _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "#basis-information",
              children: "Basis information"
            })
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.a, {
              href: "#the-building-blocks",
              children: "The building blocks"
            }), "\n", _jsxs(_components.ul, {
              children: ["\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#defining-a-fallback",
                  children: "Defining a fallback"
                })
              }), "\n"]
            }), "\n"]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.a, {
              href: "#main-theming-file",
              children: "Main theming file"
            }), "\n", _jsxs(_components.ul, {
              children: ["\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#creating-a-new-main-theme",
                  children: "Creating a new main theme"
                })
              }), "\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#run-the-portal-with-a-different-theme",
                  children: "Run the Portal with a different theme"
                })
              }), "\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#show-theme-tag-in-portal",
                  children: "Show theme tag in Portal"
                })
              }), "\n"]
            }), "\n"]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.a, {
              href: "#component-theming",
              children: "Component theming"
            }), "\n", _jsxs(_components.ul, {
              children: ["\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#button-as-an-example",
                  children: "Button as an example"
                })
              }), "\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#css-custom-properties-and-root",
                  children: "CSS custom properties and :root"
                })
              }), "\n"]
            }), "\n"]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.a, {
              href: "#local-theming-setup",
              children: "Local Theming setup"
            }), "\n", _jsxs(_components.ul, {
              children: ["\n", _jsx(_components.li, {
                children: _jsx(_components.a, {
                  href: "#yarn-link-and-sass",
                  children: "yarn link and SASS"
                })
              }), "\n"]
            }), "\n"]
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "#chrome-extension-eufemia-theme-manager",
              children: "Chrome Extension: Eufemia Theme Manager"
            })
          }), "\n"]
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Basis information"
    }), "\n", _jsxs(_components.p, {
      children: ["The default DNB brand theme is called: ", _jsx(_components.code, {
        children: "ui"
      }), " which stands for ", _jsx(_components.em, {
        children: "universal identity"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      children: "The building blocks"
    }), "\n", _jsx(_components.p, {
      children: "A theme consists of several files, which again includes SASS import declarations to single component styles."
    }), "\n", _jsxs(_components.p, {
      children: ["To include new or update component themes, run ", _jsx(_components.code, {
        children: "yarn build"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "It will find all related theme styles and put it inside the main theme file so they can get compiled to one CSS bundle."
    }), "\n", _jsx(_components.h3, {
      children: "Defining a fallback"
    }), "\n", _jsx(_components.p, {
      children: "Depending on the need of a theme, you can define a fallback to another theme."
    }), "\n", _jsx(_components.p, {
      children: "It will use that fallback theme as a basis, but use your new theme styles instead – as soon as one exists with the same name."
    }), "\n", _jsx(_components.p, {
      children: "Define a fallback inside a theme set:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.code, {
          children: "dnb-eufemia/src/style/themes/theme-sbanken/sbanken-theme-components.scss"
        })
      }), "\n"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-scss",
        children: "/**\n * ATTENTION: This file is auto generated by using \"themeFactory\".\n * But you still can change the content of this file on the very top.\n */\n\n$THEME_FALLBACK: 'ui';\n\n/**\n * NB: The content below is auto generated by the \"themeFactory\".\n * You may want to update it by running \"yarn build\" locally.\n */\n\n@import '<auto-generated>/dnb-button-theme-ui.scss';\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["and as soon as e.g. ", _jsx(_components.code, {
        children: "dnb-button-theme-sbanken.scss"
      }), " exists – it will be used instead of ", _jsx(_components.code, {
        children: "dnb-button-theme-ui.scss"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      children: "Main theming file"
    }), "\n", _jsxs(_components.p, {
      children: ["There is one ", _jsx(_components.strong, {
        children: "Main Theme File"
      }), ", that includes all properties and element styles:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.code, {
          children: "dnb-eufemia/src/style/themes/theme-ui/ui-theme-basis.scss"
        })
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Creating a new main theme"
    }), "\n", _jsx(_components.p, {
      children: "First, duplicate or create new SCSS files:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.code, {
          children: "dnb-eufemia/src/style/themes/theme-<YOUR-THEME>/<YOUR-THEME>-theme-basis.scss"
        })
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Do the same with every component (button etc.) you want to customize:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.code, {
          children: "dnb-eufemia/src/components/button/style/themes/dnb-button-theme-<YOUR-THEME>.scss"
        })
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["and run ", _jsx(_components.code, {
        children: "yarn build"
      }), ". All the related component theme file will then be created or updated."]
    }), "\n", _jsx(_components.h3, {
      children: "Run the Portal with a different theme"
    }), "\n", _jsxs(_components.p, {
      children: ["What theme gets imported is handled in the Gatsby Plugin ", _jsx(_components.a, {
        href: "https://github.com/dnbexperience/gatsby-plugin-eufemia-theme-handler",
        children: "gatsby-plugin-eufemia-theme-handler"
      }), "."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "In the Portal Tools you can switch to a different theme (runtime)."
      }), "\n", _jsxs(_components.li, {
        children: ["You can also define a different theme in the url itself ", _jsx(_components.code, {
          children: "path/?eufemia-theme=eiendom"
        }), " (runtime)."]
      }), "\n", _jsxs(_components.li, {
        children: ["Change the theme ", _jsx(_components.code, {
          children: "defaultTheme"
        }), " name in the plugin options inside ", _jsx(_components.code, {
          children: "gatsby-config"
        }), " (build-time)."]
      }), "\n", _jsxs(_components.li, {
        children: ["Create a new ", _jsx(_components.code, {
          children: "/packages/dnb-design-system-portal/.env"
        }), " file that includes e.g. ", _jsx(_components.code, {
          children: "GATSBY_EUFEMIA_THEME=eiendom"
        }), " (build-time)."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Show theme tag in Portal"
    }), "\n", _jsxs(_components.p, {
      children: ["Add the property ", _jsx(_components.code, {
        children: "theme: 'sbanken'"
      }), " to the main ", _jsx(_components.code, {
        children: ".mdx"
      }), " file for a Portal page to show a tag next to it when that theme is selected. This is currently mainly intended for Sbanken."]
    }), "\n", _jsxs(_components.p, {
      children: ["Example: ", _jsx(_components.code, {
        children: "/docs/components/button.mdx"
      })]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-mdx",
        children: "---\ntitle: 'Button'\ndescription: 'The Button component should be used as the primary call-to-action in a form, or as a user interaction mechanism.'\nshowTabs: true\ntheme: 'sbanken'\n---\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Component theming"
    }), "\n", _jsx(_components.p, {
      children: "All components have a style separation of:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.em, {
          children: "layout"
        }), " where positioning and raw shapes are defined."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.em, {
          children: "skinning"
        }), " where colors and peculiarity are defined."]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "This way we can create more easily custom skinned themes."
    }), "\n", _jsx(_components.h3, {
      children: "Button as an example"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["All the raw ", _jsx(_components.em, {
          children: "layout"
        }), " related CSS properties are in this one file ", _jsx(_components.code, {
          children: "dnb-button.scss"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["While the theme file ", _jsx(_components.code, {
          children: "dnb-button-theme-ui.scss"
        }), " contains colorization and font sizes (", _jsx(_components.em, {
          children: "skinning"
        }), ")."]
      }), "\n"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "/button/style/dnb-button.scss // layout styles\n/button/style/themes/dnb-button-theme-ui.scss // main theme styles\n/button/style/themes/dnb-button-theme-eiendom.scss// additional theme styles\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["How ", _jsx(_components.code, {
        children: "eiendom"
      }), " is set up does not matter. It could either import parts from ", _jsx(_components.code, {
        children: "ui"
      }), " or be maintained independently."]
    }), "\n", _jsx(_components.h3, {
      children: "CSS custom properties and :root"
    }), "\n", _jsx(_components.p, {
      children: "Whenever possible use Eufemia Properties inside Eufemia components."
    }), "\n", _jsxs(_components.p, {
      children: ["When defining custom properties for a component, ", _jsx(_components.strong, {
        children: "prefer"
      }), " to put them inside the component scope:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-css",
        children: ".dnb-button {\n  --text-size: var(--font-size-medium);\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In some circumstances, you may make them share-able and place them inside ", _jsx(_components.code, {
        children: ":root"
      }), ". They should then contain the component-name as a prefix."]
    }), "\n", _jsx(_components.h2, {
      children: "Local Theming setup"
    }), "\n", _jsxs(_components.p, {
      children: ["There are several solutions to ", _jsx(_components.strong, {
        children: "create a new theme"
      }), ".\nOne of which is by using the ", _jsx(_components.a, {
        href: "https://yarnpkg.com/lang/en/docs/cli/link/",
        children: "linking feature of Yarn"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "yarn link and SASS"
    }), "\n", _jsxs(_components.p, {
      children: ["Make sure your project can handle ", _jsx(_components.strong, {
        children: "*.scss"
      }), " files."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "1."
      }), " make a copy of the ", _jsx(_components.a, {
        href: "https://github.com/dnbexperience/eufemia",
        children: "repository"
      }), ". Place it somewhere locally on your machine"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "2."
      }), " change your command line (Terminal) directory to the sub package ", _jsx(_components.code, {
        children: "@dnb/eufemia"
      }), " (", _jsx(_components.em, {
        children: "eufemia/packages/eufemia"
      }), ")"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "3."
      }), " make the package ready for development by running:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-bash",
        children: "$ yarn install && yarn build && yarn link\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "4."
      }), " on your application root directory, run:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-bash",
        children: "$ yarn link \"@dnb/eufemia\"\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "5."
      }), " That's it. Now you can use (import/require) the NPM module in your application like:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-js",
        children: "import { Button } from 'dnb-eufemia/components'\n\n// See the \"src\" in the path?\nimport 'dnb-eufemia/src/style/themes/theme-ui/ui-theme-[MY THEME].scss'\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "6."
      }), " Don't forget to add ", _jsx(_components.code, {
        children: "\"@dnb/eufemia\": \"*\""
      }), " with the respective version (alongside React) to your dependencies:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-json",
        children: "  \"dependencies\": {\n    \"@dnb/eufemia\": \"x\",\n    \"react\": \"x\",\n    \"react-dom\": \"x\",\n    ...\n  }\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Chrome Extension: Eufemia Theme Manager"
    }), "\n", _jsxs(_components.p, {
      children: ["Use the ", _jsx(_components.a, {
        href: "https://chrome.google.com/webstore/detail/eufemia-theme-manager/pijolaebmeacaekbhoefjmhogckdcclb",
        children: "Chrome Browser Extension"
      }), " to:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "test themes on web applications"
      }), "\n", _jsx(_components.li, {
        children: "create new possible themes"
      }), "\n", _jsx(_components.li, {
        children: "look how the outcome would be if a theme would be used"
      }), "\n", _jsx(_components.li, {
        children: "and create areas where a different or a modified theme would make more sense"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["You can also download the ", _jsx(_components.a, {
        href: "https://github.com/dnbexperience/eufemia-theme-manager/raw/main/eufemia-theme-manager-extension/web-ext-artifacts/eufemia_theme_manager-latest.zip",
        children: "Chrome Browser Extension (ZIP)"
      }), ", and install it manually in your browser. To do so, go to ", _jsx(_components.code, {
        children: "chrome://extensions"
      }), " and drag & drop the downloaded ZIP file in the opened extensions tab."]
    }), "\n", _jsxs(_components.p, {
      children: ["Contributions are welcome. Heres the ", _jsx(_components.a, {
        href: "https://github.com/dnbexperience/eufemia-theme-manager",
        children: "source code"
      }), "."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
